import { useEffect, useRef, useState } from "react";

function easeOutQuart(t) {
  return 1 - (1 - t) ** 4;
}

function formatValue(num, useFormat) {
  return useFormat ? num.toLocaleString("en-US") : String(num);
}

export default function AnimatedCounter({
  value,
  suffix = "",
  prefix = "",
  duration = 2800,
  format = false,
  className = "",
  as: Tag = "span",
}) {
  const ref = useRef(null);
  const [display, setDisplay] = useState(0);
  const rafRef = useRef(0);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    const stopAnimation = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    };

    const resetCounter = () => {
      stopAnimation();
      setDisplay(0);
    };

    const startAnimation = () => {
      stopAnimation();

      if (value <= 0) {
        setDisplay(0);
        return;
      }

      setDisplay(0);
      const startTime = performance.now();
      const tick = (now) => {
        const progress = Math.min(1, (now - startTime) / duration);
        const eased = easeOutQuart(progress);
        const next = progress < 1 ? Math.floor(value * eased) : value;
        setDisplay(next);
        if (progress < 1) {
          rafRef.current = requestAnimationFrame(tick);
        } else {
          rafRef.current = 0;
        }
      };
      rafRef.current = requestAnimationFrame(tick);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          startAnimation();
        } else {
          resetCounter();
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -40px 0px" }
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
      stopAnimation();
    };
  }, [value, duration]);

  return (
    <Tag ref={ref} className={className}>
      {prefix}
      {formatValue(display, format)}
      {suffix}
    </Tag>
  );
}
